import { useMemo, useState } from "react";
import {
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  List,
  Search,
  X,
} from "lucide-react";
import { inputClass, selectClass } from "./FormField";

export type PackageActLimit = {
  quantity: string;
  period: "" | "per_day" | "per_week" | "per_month" | "per_year";
  notes: string;
};

export const emptyActLimit = (): PackageActLimit => ({
  quantity: "",
  period: "",
  notes: "",
});

type PackageIncludedActsStepProps = {
  acts: Array<{ id: string; code: string; label: string; costUsd?: string }>;
  labels: SafecheckTranslations["packages"]["create"]["includedActs"];
  search: string;
  onSearchChange: (value: string) => void;
  includedActIds: string[];
  onIncludedActIdsChange: (ids: string[]) => void;
  actLimits: Record<string, PackageActLimit>;
  onActLimitsChange: (limits: Record<string, PackageActLimit>) => void;
  loading?: boolean;
};

export function PackageIncludedActsStep({
  acts,
  labels,
  search,
  onSearchChange,
  includedActIds,
  onIncludedActIdsChange,
  actLimits,
  onActLimitsChange,
  loading = false,
}: PackageIncludedActsStepProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const hospitalActs = acts;

  const filteredActs = useMemo(() => {
    if (!search) return hospitalActs;
    const query = search.toLowerCase();
    return hospitalActs.filter(
      (act) =>
        act.code.toLowerCase().includes(query) || act.label.toLowerCase().includes(query),
    );
  }, [hospitalActs, search]);

  const includedSet = useMemo(() => new Set(includedActIds), [includedActIds]);
  const includedCount = hospitalActs.filter((act) => includedSet.has(act.id)).length;
  const outOfPackageCount = hospitalActs.length - includedCount;

  const updateLimit = (actId: string, patch: Partial<PackageActLimit>) => {
    onActLimitsChange({
      ...actLimits,
      [actId]: { ...emptyActLimit(), ...actLimits[actId], ...patch },
    });
  };

  const toggleAct = (actId: string) => {
    if (includedSet.has(actId)) {
      onIncludedActIdsChange(includedActIds.filter((id) => id !== actId));
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(actId);
        return next;
      });
      return;
    }
    onIncludedActIdsChange([...includedActIds, actId]);
    setExpandedIds((prev) => new Set(prev).add(actId));
    if (!actLimits[actId]) {
      updateLimit(actId, {});
    }
  };

  const toggleExpanded = (actId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actId)) next.delete(actId);
      else next.add(actId);
      return next;
    });
  };

  const selectAll = () => {
    onIncludedActIdsChange(filteredActs.map((act) => act.id));
    setExpandedIds(new Set(filteredActs.map((act) => act.id)));
    const next = { ...actLimits };
    for (const act of filteredActs) {
      if (!next[act.id]) next[act.id] = emptyActLimit();
    }
    onActLimitsChange(next);
  };

  const deselectAll = () => {
    const filteredIds = new Set(filteredActs.map((act) => act.id));
    onIncludedActIdsChange(includedActIds.filter((id) => !filteredIds.has(id)));
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const id of filteredIds) next.delete(id);
      return next;
    });
  };

  const selectedCountLabel = labels.selectedCount
    .replace("{selected}", String(includedCount))
    .replace("{total}", String(hospitalActs.length));

  if (loading) {
    return <p className="text-sm text-muted">Chargement des actes…</p>;
  }

  return (
    <div>
      <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
        <List size={16} className="text-primary" />
        {labels.title}
      </div>

      <div className="mb-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3 text-sm text-foreground">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
          <span className="text-xs font-bold">i</span>
        </span>
        <p>{labels.instructions}</p>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex items-center justify-between rounded-xl border border-border bg-purple-soft/50 px-4 py-3">
          <div>
            <p className="text-xs text-muted">{labels.available}</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{hospitalActs.length}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-soft">
            <List size={18} className="text-purple" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border bg-teal-soft/50 px-4 py-3">
          <div>
            <p className="text-xs text-muted">{labels.included}</p>
            <p className="mt-1 text-2xl font-bold text-green">{includedCount}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-soft">
            <Check size={18} className="text-green" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border bg-yellow-soft/50 px-4 py-3">
          <div>
            <p className="text-xs text-muted">{labels.outOfPackage}</p>
            <p className="mt-1 text-2xl font-bold text-yellow">{outOfPackageCount}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-soft">
            <X size={18} className="text-yellow" />
          </div>
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={labels.searchPlaceholder}
          className={`${inputClass} pl-9`}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={selectAll}
            disabled={filteredActs.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            <CheckCheck size={15} />
            {labels.selectAll}
          </button>
          <button
            type="button"
            onClick={deselectAll}
            disabled={filteredActs.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X size={15} />
            {labels.deselectAll}
          </button>
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
          {selectedCountLabel}
        </p>
      </div>

      {filteredActs.length === 0 ? (
        <div className="rounded-xl border border-border bg-card-muted/20 px-4 py-16 text-center text-sm text-muted">
          {labels.emptyHospital}
        </div>
      ) : (
        <div className="max-h-[520px] overflow-y-auto rounded-xl border border-border">
          {filteredActs.map((act) => {
            const isIncluded = includedSet.has(act.id);
            const isExpanded = isIncluded && expandedIds.has(act.id);
            const limit = actLimits[act.id] ?? emptyActLimit();

            return (
              <div
                key={act.id}
                className="border-b border-border last:border-0 hover:bg-card-muted/20"
              >
                <label className="flex cursor-pointer items-start gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isIncluded}
                    onChange={() => toggleAct(act.id)}
                    className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-primary">{act.code}</p>
                    <p className="mt-0.5 text-sm text-foreground">{act.label}</p>
                  </div>
                  <span className="shrink-0 text-sm text-muted">
                    {act.costUsd != null ? `${act.costUsd} USD` : "—"}
                  </span>
                </label>

                {isIncluded && (
                  <div className="px-4 pb-3 pl-11">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(act.id)}
                      className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {labels.limits.toggle}
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card-muted/20 p-3 sm:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                            {labels.limits.quantity}
                          </label>
                          <input
                            type="number"
                            min={1}
                            placeholder={labels.limits.quantityPlaceholder}
                            value={limit.quantity}
                            onChange={(event) =>
                              updateLimit(act.id, { quantity: event.target.value })
                            }
                            className={`${inputClass} text-sm`}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                            {labels.limits.period}
                          </label>
                          <select
                            value={limit.period}
                            onChange={(event) =>
                              updateLimit(act.id, {
                                period: event.target.value as PackageActLimit["period"],
                              })
                            }
                            className={`${selectClass} text-sm`}
                          >
                            <option value="">{labels.limits.periodNone}</option>
                            <option value="per_day">{labels.limits.perDay}</option>
                            <option value="per_week">{labels.limits.perWeek}</option>
                            <option value="per_month">{labels.limits.perMonth}</option>
                            <option value="per_year">{labels.limits.perYear}</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                            {labels.limits.notes}
                          </label>
                          <input
                            type="text"
                            placeholder={labels.limits.notesPlaceholder}
                            value={limit.notes}
                            onChange={(event) =>
                              updateLimit(act.id, { notes: event.target.value })
                            }
                            className={`${inputClass} text-sm`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
